import isNil from 'lodash/isNil';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ImpersonationData,
  loadImpersonationHeader,
  saveImpersonationHeader,
  deleteImpersonationHeader,
  ImpersonationHeader,
  maybeAttachSavedImpersonationHeader,
  findImpersonationHeader,
  IMPERSONATION_STORAGE_KEY,
} from 'utils/impersonation';
import {
  APP_ROUTES,
} from 'views/EMR/router';
import FullScreenSpinner from 'core/components/FullScreenSpinner';

type ImpersonationContextProps = {
  impersonation: ImpersonationData | null;
  isImpersonating: boolean;
  startImpersonation: () => void;
  stopImpersonation: () => void;
  updateImpersonation: (newHeader: ImpersonationHeader) => void;
  maybeAttachSavedImpersonationHeader: (
    headers: Record<string, string>,
  ) => void;
  findImpersonationHeader: (
    headers: Record<string, string>,
  ) => ImpersonationHeader | null;
};

const ImpersonationContext = createContext<ImpersonationContextProps>({
  impersonation: null,
  isImpersonating: false,
  startImpersonation: () => {},
  stopImpersonation: () => {},
  updateImpersonation: () => {},
  maybeAttachSavedImpersonationHeader,
  findImpersonationHeader,
});

export function ImpersonationProvider({ children }: PropsWithChildren) {
  const [impersonation, setImpersonation] = useState<ImpersonationData | null>(
    () => {
      const savedHeader = loadImpersonationHeader();
      const impersonationData = ImpersonationData.fromHeader(savedHeader);
      return isNil(impersonationData) ? null : impersonationData;
    },
  );

  const lastHeaderRef = useRef(loadImpersonationHeader());
  const shouldRedirect = !isNil(impersonation) && impersonation.isExpired();

  const isImpersonating = useMemo(
    () => impersonation !== null && !impersonation.isExpired(),
    [impersonation],
  );

  const startImpersonation = useCallback(() => {
    setImpersonation(ImpersonationData.fromLoader(loadImpersonationHeader));
  }, []);

  const redirectToSupportRoot = useCallback(() => {
    window.location.replace(APP_ROUTES.POPULATE_SUPPORT.ROOT);
  }, []);

  const stopImpersonation = useCallback(() => {
    deleteImpersonationHeader();
    // Redirect immediately without updating React state to prevent
    // components from re-rendering and making API calls without the
    // impersonation header before the navigation completes.
    // The page reload will start fresh without any impersonation data.
    redirectToSupportRoot();
  }, [redirectToSupportRoot]);

  const onStorage = (event: StorageEvent) => {
    if (event.storageArea === localStorage && event.key === IMPERSONATION_STORAGE_KEY) {
    // `loadImpersonationHeader()` already reads from localStorage; using it
      // keeps behavior consistent regardless of event.newValue serialization.
      const latestHeader = loadImpersonationHeader();
      const prevHeader = lastHeaderRef.current;
      lastHeaderRef.current = latestHeader;

      setImpersonation(ImpersonationData.fromHeader(latestHeader));

      // If impersonation was terminated in another tab, force a full navigation
      // to support login page to avoid stale EMR UI crashing.
      if (!isNil(prevHeader) && isNil(latestHeader)) {
        stopImpersonation();
      }
    }
  };
  // Keep impersonation state in sync across browser tabs/windows.
  // When one tab updates/removes the stored impersonation header, other tabs
  // receive a `storage` event and update their in-memory state accordingly.
  useEffect(() => {
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  const updateImpersonation = useCallback((newHeader: ImpersonationHeader) => {
    const savedHeader = loadImpersonationHeader();
    if (savedHeader === newHeader) return;

    saveImpersonationHeader(newHeader);
    setImpersonation(ImpersonationData.fromHeader(newHeader));
  }, []);

  useEffect(() => {
    if (shouldRedirect) {
      stopImpersonation();
    }
  }, [shouldRedirect, stopImpersonation]);

  // Check for expiry periodically
  useEffect(() => {
    if (!impersonation) return undefined;

    const checkExpiry = () => {
      if (impersonation.isExpired()) {
        stopImpersonation();
      }
    };

    const intervalId = setInterval(checkExpiry, 60_000 /* 1 minute */);
    return () => clearInterval(intervalId);
  }, [impersonation, stopImpersonation]);

  const value = useMemo(
    () => ({
      impersonation,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      updateImpersonation,
      maybeAttachSavedImpersonationHeader,
      findImpersonationHeader,
    }),
    [
      impersonation,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      updateImpersonation,
      maybeAttachSavedImpersonationHeader,
      findImpersonationHeader,
    ],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {shouldRedirect ? <FullScreenSpinner /> : children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

export default ImpersonationContext;
